import React from 'react';
import { Card } from '@/components/ui/card';
import { buildDays, pyWeekday, dayFull, fullDate } from '@/lib/dates';

// Farby pre časovú os (osvedčená konvencia): ranná = zelená, poobedná = fialová, víkend = tyrkysová
const BAR = {
  R: '#22C55E',
  M: '#F59E0B',
  P: '#A855F7',
  C: '#F43F5E',
  SO: '#14B8A6',
  NE: '#14B8A6',
};
const OFF_LABEL = { X: 'Voľno', D: 'Dovolenka', '': 'Voľno' };
const HOURS = Array.from({ length: 24 }, (_, h) => h);

function parseH(t) {
  return t ? parseInt(t.split(':')[0], 10) : null;
}

export default function TimelineView({ schedule, employees, workplace }) {
  if (!schedule || !workplace) return null;
  const days = buildDays(schedule.start_date, schedule.num_days);
  const assignments = schedule.assignments || {};
  const compRest = schedule.stats?.comp_rest || {};
  const shiftByCode = Object.fromEntries((workplace.shifts || []).map((s) => [s.code, s]));

  return (
    <div className="space-y-5" data-testid="timeline-view">
      {days.map((iso) => {
        const wd = pyWeekday(iso);
        const oh = (workplace.opening_hours || {})[String(wd)];
        const openH = oh ? parseH(oh.open) : null;
        const closeH = oh ? parseH(oh.close) : null;
        const isOpen = (h) => openH !== null && h >= openH && h < closeH;

        // počet ľudí v práci po hodinách
        const cover = HOURS.map((h) =>
          employees.reduce((n, e) => {
            const code = (assignments[e.id] || {})[iso] || 'X';
            const sh = shiftByCode[code];
            if (sh && parseH(sh.start) <= h && h < parseH(sh.end)) return n + 1;
            return n;
          }, 0)
        );

        const codesToday = employees.map((e) => (assignments[e.id] || {})[iso] || 'X');
        const cntR = codesToday.filter((c) => c === 'R').length;
        const cntP = codesToday.filter((c) => c === 'P').length;
        const cntW = codesToday.filter((c) => c === 'SO' || c === 'NE').length;

        return (
          <Card key={iso} className="p-4" data-testid={`timeline-day-${iso}`}>
            <div className="flex items-baseline gap-2 mb-2 flex-wrap">
              <span className="font-heading font-semibold text-slate-900">{fullDate(iso)}</span>
              <span className="text-sm text-slate-500">{dayFull(iso)}</span>
              {!oh && <span className="text-xs text-slate-400">· zatvorené</span>}
              <span className="ml-auto flex gap-2 text-xs">
                {cntR > 0 && <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>Ranná: {cntR}</span>}
                {cntP > 0 && <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F3E8FF', color: '#6B21A8' }}>Poobedná: {cntP}</span>}
                {cntW > 0 && <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: '#CCFBF1', color: '#115E59' }}>Víkend: {cntW}</span>}
              </span>
            </div>

            {/* hodinové pravítko */}
            <div className="flex items-center">
              <div className="w-40 shrink-0" />
              <div className="flex-1 flex">
                {HOURS.map((h) => (
                  <div key={h} className="flex-1 text-center text-[10px] text-slate-400 font-mono border-l border-slate-100 first:border-l-0">{h}</div>
                ))}
              </div>
            </div>

            {/* počet ľudí v prevádzke */}
            <div className="flex items-center mb-1">
              <div className="w-40 shrink-0 text-[11px] text-slate-400 uppercase tracking-wide">Počet v práci</div>
              <div className="flex-1 flex">
                {HOURS.map((h) => (
                  <div key={h} className={`flex-1 text-center text-[10px] font-mono py-0.5 border-l border-slate-100 first:border-l-0 ${isOpen(h) && cover[h] === 0 ? 'bg-red-50 text-red-600 font-bold' : cover[h] > 0 ? 'text-slate-700 font-semibold' : 'text-slate-300'}`}>
                    {cover[h] || ''}
                  </div>
                ))}
              </div>
            </div>

            {/* riadky zamestnancov */}
            <div className="space-y-1">
              {employees.map((emp) => {
                const code = (assignments[emp.id] || {})[iso] || 'X';
                const sh = shiftByCode[code];
                const off = !sh;
                const isNV = code === 'X' && (compRest[emp.id] || []).includes(iso);
                const sH = sh ? parseH(sh.start) : 0;
                const eH = sh ? parseH(sh.end) : 0;
                return (
                  <div key={emp.id} className="flex items-center" data-testid={`tl-row-${emp.id}-${iso}`}>
                    <div className={`w-40 shrink-0 text-xs truncate pr-2 ${emp.is_virtual ? 'text-amber-600 font-medium' : 'text-slate-700'}`} title={emp.name}>
                      {emp.is_virtual && '● '}{emp.name}
                    </div>
                    <div className="flex-1 relative h-5 rounded overflow-hidden">
                      {/* podklad: zatvorené = tmavosivá, otvorené = svetlosivá */}
                      <div className="absolute inset-0 flex">
                        {HOURS.map((h) => (
                          <div key={h} className="flex-1" style={{ backgroundColor: isOpen(h) ? '#EEF1F4' : '#B6BCC4' }} />
                        ))}
                      </div>
                      {off ? (
                        <div className={`absolute inset-0 flex items-center justify-center text-[10px] font-semibold tracking-widest ${isNV ? 'text-green-700' : 'text-slate-500'}`}>
                          {code === 'D' ? 'DOVOLENKA' : isNV ? 'NÁHR. VOĽNO' : 'VOĽNO'}
                        </div>
                      ) : (
                        <div
                          className="absolute top-0.5 bottom-0.5 rounded-full"
                          style={{ left: `${(sH / 24) * 100}%`, width: `${((eH - sH) / 24) * 100}%`, backgroundColor: BAR[code] || '#64748B' }}
                          title={`${sh.name} ${sh.start}–${sh.end}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export function TimelineLegend() {
  const items = [
    ['#22C55E', 'Ranná'],
    ['#A855F7', 'Poobedná'],
    ['#F59E0B', 'Medzizmena'],
    ['#F43F5E', 'Celodenná'],
    ['#14B8A6', 'Víkend'],
    ['#B6BCC4', 'Zatvorené'],
    ['#EEF1F4', 'Otvorené (mimo zmeny)'],
  ];
  return (
    <div className="flex flex-wrap gap-3 text-xs text-slate-600">
      {items.map(([c, l]) => (
        <span key={l} className="inline-flex items-center gap-1.5">
          <span className="w-4 h-3 rounded-sm border border-slate-200" style={{ backgroundColor: c }} />{l}
        </span>
      ))}
    </div>
  );
}
